import z from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input';
import {
  legalFormsIn,
  type ExtraFieldOf,
  type LegalForm,
} from '@vytal/domain/legal-forms';
import { BusinessType } from '@/graphql/generatedTypes';

type Equals<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2
    ? true
    : false;
type Assert<T extends true> = T;
// Fails to compile when colugo's BusinessType and @vytal/domain drift apart.
export type BusinessTypeIsLegalForm = Assert<
  Equals<`${BusinessType}`, LegalForm>
>;

// Sound because of the assertion above: the enum's values are the legal forms.
export const toBusinessType = (legalForm: LegalForm) =>
  legalForm as BusinessType;

export const addressSchema = z.object({
  street: z.string().nonempty(),
  extra: z.string().optional(),
  zip: z.string().nonempty(),
  city: z.string().nonempty(),
  countryCode: z.string().nonempty(),
  state: z.string().optional(),
});

const commonFields = z.object({
  comment: z.string().optional(),
  firstName: z.string().nonempty(),
  lastName: z.string().nonempty(),
  companyName: z.string().nonempty(),
  email: z.email(),
  phoneNumber: z.string().refine(isValidPhoneNumber),
  address: addressSchema,
});

const privatePersonFields = {
  dateOfBirth: z.date(),
} satisfies Record<ExtraFieldOf<'privatePerson'>, z.ZodType>;

const companyFields = {
  vatId: z.string().nonempty(),
  corporateStructureInfo: z.string(),
} satisfies Record<
  ExtraFieldOf<'partnership'> | ExtraFieldOf<'corporation'>,
  z.ZodType
>;

export const orderFormSchema = z.discriminatedUnion('businessType', [
  commonFields.extend({
    businessType: z.enum(legalFormsIn('privatePerson')),
    ...privatePersonFields,
  }),
  commonFields.extend({
    businessType: z.enum([
      ...legalFormsIn('partnership'),
      ...legalFormsIn('corporation'),
    ]),
    ...companyFields,
  }),
]);

export type OrderFormValues = z.infer<typeof orderFormSchema>;

export const defaultFormValues: Partial<OrderFormValues> = {
  corporateStructureInfo: '',
  vatId: '',
  comment: '',
  address: {
    countryCode: 'DE',
    street: '',
    extra: '',
    zip: '',
    city: '',
  },
};
